export const queryKeys = {
  sportsPayOnboarding: {
    all: ['sportsPayOnboarding'],
    byOrg: (organizationId) => [
      ...queryKeys.sportsPayOnboarding.all,
      organizationId,
    ],
  },
  teamRosterPlayers: {
    all: ['teamRosterPlayers'],
    byTeam: (teamId) => [...queryKeys.teamRosterPlayers.all, teamId],
    byRoster: (teamId, rosterId) => [
      ...queryKeys.teamRosterPlayers.byTeam(teamId),
      rosterId,
    ],
    // Single roster player (GET .../players/:playerId), used to seed the Edit
    // Player form. Deliberately nested UNDER byRoster so React Query's prefix
    // matching makes every existing list invalidation refresh the detail too.
    byPlayer: (teamId, rosterId, playerId) => [
      ...queryKeys.teamRosterPlayers.byRoster(teamId, rosterId),
      playerId,
    ],
  },
  operations: {
    all: ['operations'],
    tree: (scheduleId) => [...queryKeys.operations.all, 'tree', scheduleId],
    // Standings for a division or conference panel. Keyed by the schedule's own
    // id AND the groupBy it was fetched with — the same id queried at two
    // levels is two different responses, so they must not share a cache entry.
    standings: (scheduleId, groupBy = 'Division') => [
      ...queryKeys.operations.all,
      'standings',
      scheduleId ?? null,
      groupBy,
    ],
    // A team's rosters as the Operations panel MAPS them (`TeamRosterSummary[]`).
    // Deliberately not `teamRosters.byTeam`: that caches the same endpoint's RAW
    // rosters for the Team page, and two shapes under one key would each read
    // the other's data.
    teamRosters: (teamId) => [
      ...queryKeys.operations.all,
      'teamRosters',
      teamId,
    ],
  },
  organizations: {
    all: ['organizations'],
    byId: (organizationId) => [...queryKeys.organizations.all, organizationId],
  },
  team: {
    all: ['team'],
    // A single team's full detail payload — images, contact fields, flags.
    // The operations TREE carries only what its rows draw, so the edit modal
    // reads this instead of seeding itself from a tree node.
    byId: (teamId) => [...queryKeys.team.all, teamId ?? null],
  },
  game: {
    all: ['game'],
    byId: (gameId, isPublic) => [...queryKeys.game.all, gameId, !!isPublic],
  },
  // Raw /games/:id payload (unparsed) for screens that read the API shape
  // directly (e.g. Game Attendance reads sport_id / generic_sport_id). Kept
  // separate from `game.byId` so it never collides with useGame's parsed +
  // redux-mirrored cache.
  gameRaw: {
    all: ['gameRaw'],
    byId: (gameId) => [...queryKeys.gameRaw.all, gameId ?? null],
  },
  teamRecord: {
    all: ['teamRecord'],
    byId: (teamId) => [...queryKeys.teamRecord.all, teamId ?? null],
  },
  notification: {
    all: ['notification'],
    byId: (notificationId) => [
      ...queryKeys.notification.all,
      notificationId ?? null,
    ],
  },
  finderSettings: {
    all: ['finderSettings'],
    byPlayer: (playerId) => [...queryKeys.finderSettings.all, playerId ?? null],
  },
  teamFinder: {
    all: ['teamFinder'],
    search: (filters) => [...queryKeys.teamFinder.all, filters ?? null],
  },
  // The Schedule > Games grid (List / Calendar / Details). Deliberately its OWN
  // root, never nested under `game.all`: React Query matches by key PREFIX, so
  // any existing `invalidateQueries(game.all)` — the game page, the scoring
  // screens — would refetch the whole paginated grid and throw away the admin's
  // scroll position and selection mid-edit.
  scheduleGames: {
    all: ['scheduleGames'],
    // `fingerprint` is a plain object; react-query stable-hashes it, so two
    // filter combinations can never collide and a stale response can never be
    // rendered under a newer key.
    byFingerprint: (fingerprint) => [
      ...queryKeys.scheduleGames.all,
      fingerprint ?? null,
    ],
  },
  // The day index behind the calendar's opening month (GET /games/dates). Its
  // own root rather than a `scheduleGames` child: invalidating the grid after a
  // bulk retime must not also refetch this, and a stale day index is harmless —
  // it decides which month to OPEN on, not what that month contains.
  scheduleGameDates: {
    all: ['scheduleGameDates'],
    byFingerprint: (fingerprint) => [
      ...queryKeys.scheduleGameDates.all,
      fingerprint ?? null,
    ],
  },
  // Location/facility options behind the add/edit Game modal's pickers.
  // `scheduleId` and `recursive` both change what the endpoint returns, so both
  // are part of the key -- the "All Locations" / "Within organization" toggle
  // flips `recursive` and must not read the other list's cache.
  venues: {
    all: ['venues'],
    bySchedule: (scheduleId, recursive) => [
      ...queryKeys.venues.all,
      scheduleId ?? null,
      recursive ?? true,
    ],
  },
  venueFacilities: {
    all: ['venueFacilities'],
    byVenue: (venueId) => [...queryKeys.venueFacilities.all, venueId],
  },
  // The competition's child levels (GET /schedules/:id/children/dropdown).
  // Only fetched when a placeholder team is selected, which is the one case
  // the admin has to name a division by hand.
  subCompetitions: {
    all: ['subCompetitions'],
    bySchedule: (scheduleId) => [
      ...queryKeys.subCompetitions.all,
      scheduleId ?? null,
    ],
  },
  // Team options for the home/visiting pickers (GET /schedules/:id/teams/simple).
  scheduleTeams: {
    all: ['scheduleTeams'],
    bySchedule: (scheduleId) => [...queryKeys.scheduleTeams.all, scheduleId],
  },
  // The officials an admin can assign to a game, scoped to one organization +
  // competition. Its own root: this is a slow-moving directory of people, not
  // part of any game's state, so a bulk retime must not throw it away.
  adminOfficialSearch: {
    all: ['adminOfficialSearch'],
    byScope: (organizationId, scheduleId) => [
      ...queryKeys.adminOfficialSearch.all,
      organizationId ?? null,
      scheduleId ?? null,
    ],
  },
  gameRosters: {
    all: ['gameRosters'],
    byGame: (gameId) => [...queryKeys.gameRosters.all, gameId],
  },
  // One roster's players + team officials (GET /games/:id/rosters/:rosterId) —
  // a different endpoint and shape from gameRosterPlayers' attendance list.
  gameRoster: {
    all: ['gameRoster'],
    byRoster: (gameId, rosterId) => [
      ...queryKeys.gameRoster.all,
      gameId ?? null,
      rosterId ?? null,
    ],
  },
  gameRosterPlayers: {
    all: ['gameRosterPlayers'],
    byRoster: (gameId, gameRosterId) => [
      ...queryKeys.gameRosterPlayers.all,
      gameId,
      gameRosterId,
    ],
  },
  gameRosterUsers: {
    all: ['gameRosterUsers'],
    byRoster: (gameId, gameRosterId) => [
      ...queryKeys.gameRosterUsers.all,
      gameId,
      gameRosterId,
    ],
  },
  player: {
    all: ['player'],
    byId: (playerId) => [...queryKeys.player.all, playerId],
  },
  // Self-scoped player profile for the Edit Player flow. Distinct from
  // `player.byId` because it hits GET /users/me/players/:id (a different
  // endpoint/permission scope than the public GET /players/:id used by the
  // Player page) — sharing a key would collide and cross-invalidate.
  playerProfile: {
    all: ['playerProfile'],
    byId: (playerId) => [...queryKeys.playerProfile.all, playerId],
  },
  playerHighlights: {
    all: ['playerHighlights'],
    byPlayer: (playerId) => [...queryKeys.playerHighlights.all, playerId],
    byProfilePlayers: (playerIds) => [
      ...queryKeys.playerHighlights.all,
      'profile',
      playerIds,
    ],
    // The horizontal "Career Highlights" list (ProfileHighlightsList) filters
    // and sorts differently than the grid tab, so it gets its own key to avoid
    // colliding with byProfilePlayers in the cache.
    byProfileList: (playerIds) => [
      ...queryKeys.playerHighlights.all,
      'profile-list',
      playerIds,
    ],
  },
  playerSeasonStats: {
    all: ['playerSeasonStats'],
    byPlayerSport: (playerId, sportId, genericSportId, goalie) => [
      ...queryKeys.playerSeasonStats.all,
      playerId ?? null,
      sportId ?? null,
      genericSportId ?? null,
      !!goalie,
    ],
    /**
     * The web Overview's career summary: skater and goalie lists cached
     * together as { player, goalie }. A key of its own — sharing
     * `byPlayerSport` handed it the Stats tab's single-role array.
     */
    careerSummary: (playerId, sportId, genericSportId) => [
      ...queryKeys.playerSeasonStats.all,
      'careerSummary',
      playerId ?? null,
      sportId ?? null,
      genericSportId ?? null,
    ],
  },
  playerStats: {
    all: ['playerStats'],
    progressSports: (playerId) => [
      ...queryKeys.playerStats.all,
      'progressSports',
      playerId ?? null,
    ],
    progress: (playerId, sportId, genericSportId, role, range, timezone) => [
      ...queryKeys.playerStats.all,
      'progress',
      playerId ?? null,
      sportId ?? null,
      genericSportId ?? null,
      role ?? null,
      range ?? null,
      timezone ?? null,
    ],
    recentGames: (playerId, sportId, role) => [
      ...queryKeys.playerStats.all,
      'recentGames',
      playerId ?? null,
      sportId ?? null,
      role ?? null,
    ],
    scheduleGames: (playerId, scheduleId, role) => [
      ...queryKeys.playerStats.all,
      'scheduleGames',
      playerId ?? null,
      scheduleId ?? null,
      role ?? null,
    ],
  },
  userPlayers: {
    all: ['userPlayers'],
    me: () => [...queryKeys.userPlayers.all, 'me'],
  },
  userFamily: {
    all: ['userFamily'],
    me: () => [...queryKeys.userFamily.all, 'me'],
  },
  playerFamily: {
    all: ['playerFamily'],
    // `scopeKey` (from familyScopeKey) separates the same player's family read
    // via the self-scoped routes from the roster-scoped ones — different
    // endpoints, so they must not share a cache entry or an invalidation.
    // Defaults to 'me' so existing callers keep their current key shape.
    byPlayer: (playerId, scopeKey = 'me') => [
      ...queryKeys.playerFamily.all,
      scopeKey,
      playerId,
    ],
  },
  tutorial: {
    all: ['tutorial'],
    byKey: (key) => [...queryKeys.tutorial.all, key],
  },
  selfUser: {
    all: ['selfUser'],
    me: () => [...queryKeys.selfUser.all, 'me'],
  },
  highlight: {
    all: ['highlight'],
    byId: (highlightId) => [...queryKeys.highlight.all, highlightId],
  },
  myRegistrations: {
    all: ['myRegistrations'],
  },
  // Sep 24 — a registration session as the server holds it now. The confirmation
  // step reads the rep's Team Budget payment from it; the wizard context's copy is
  // stale once the pay page has been.
  registrationSession: {
    all: ['registrationSession'],
    byId: (sessionId) => [...queryKeys.registrationSession.all, sessionId],
  },
  userSuspensions: {
    all: ['userSuspensions'],
    me: (onlyActive) => [
      ...queryKeys.userSuspensions.all,
      'me',
      onlyActive ? 1 : 0,
    ],
  },
  pendingInstallments: {
    all: ['pendingInstallments'],
  },
  pendingDeposits: {
    all: ['pendingDeposits'],
  },
  userBilling: {
    all: ['userBilling'],
    byId: (billingId) => [...queryKeys.userBilling.all, billingId],
  },
  gameTimeline: {
    all: ['gameTimeline'],
    byGame: (gameId) => [...queryKeys.gameTimeline.all, gameId],
  },
  gameEvents: {
    all: ['gameEvents'],
    byGame: (gameId) => [...queryKeys.gameEvents.all, gameId],
  },
  events: {
    all: ['events'],
    byId: (eventId) => [...queryKeys.events.all, eventId],
    // Nested under `all` so invalidating the namespace also drops the lists.
    list: (...args) => [...queryKeys.events.all, 'list', ...args],
    byRoster: (teamId, rosterId) =>
      queryKeys.events.list(teamId ?? null, rosterId ?? null),
    // Distinct tail from byRoster so "All Seasons" never collides with a
    // roster-scoped entry for the same team.
    byTeam: (teamId) => queryKeys.events.list(teamId ?? null, 'all-seasons'),
    // My Schedule's cross-team list. The whole filter object is part of the
    // identity — every filter is sent to the endpoint, so two different filter
    // sets are two different responses and must not share a cache entry.
    byMembership: (query) => queryKeys.events.list('membership', query ?? null),
    // A competition/season/division's full list, which answers for everyone
    // rather than the viewer's own memberships. Distinct tail from
    // byMembership so the two can never share an entry: the same filters sent
    // to the two endpoints give different answers.
    bySchedule: (scheduleId, query) =>
      queryKeys.events.list('schedule', scheduleId ?? null, query ?? null),

    // ---- Paginated stream keys ----
    //
    // A DISTINCT tail from every key above, and that separation is load-bearing.
    // The stream hooks are infinite queries, so react-query stores a
    // `{ pages, pageParams }` envelope under their key; the fetch-all hooks
    // beside them store a flat `SnEvent[]`. Sharing a key would let one read
    // the other's shape — a cache collision with no error, just wrong data.
    //
    // The team keys also carry a `query` the fetch-all ones do not: those
    // endpoints took no params, so the window and sort were applied on arrival.
    // A paged stream sends them, which makes them part of the identity.
    // Nested under `list`, NOT under `all` directly. React Query matches by key
    // prefix and `useEventMutations` invalidates `events.list()` — a key rooted
    // at `[...all, 'stream']` would sit OUTSIDE that prefix, so creating or
    // editing an event would silently leave the paginated lists stale.
    streamList: (...args) => queryKeys.events.list('stream', ...args),
    byTeamStream: (teamId, query) =>
      queryKeys.events.streamList(teamId ?? null, 'all-seasons', query ?? null),
    byRosterStream: (teamId, rosterId, query) =>
      queryKeys.events.streamList(
        teamId ?? null,
        rosterId ?? null,
        query ?? null
      ),
    byScheduleStream: (scheduleId, query) =>
      queryKeys.events.streamList(
        'schedule',
        scheduleId ?? null,
        query ?? null
      ),
    byMembershipStream: (query) =>
      queryKeys.events.streamList('membership', query ?? null),

    // The opening-tab decision: one row, from today onward. Its own tail because
    // it asks a different question than any list above and must not share their
    // cache — a list is bounded by the SELECTED period, which is exactly what
    // makes it unable to answer this. Still under `list`, so an event mutation
    // invalidates it too: creating an event can change which tab should open.
    decision: (...args) => queryKeys.events.list('decision', ...args),

    // "Does this schedule hold ANY events?" — no window at all, unlike
    // `decision`, which asks only about today onward. Backs the Schedule tab's
    // wizard-vs-list gate. Under `list` like the rest, so creating a
    // competition's first event flips the gate without a reload.
    exists: (...args) => queryKeys.events.list('exists', ...args),
  },
  // The home screen's combined games + events feed (GET /home/calendar).
  //
  // Its own root rather than a child of `events`: the response carries games
  // too, so an event mutation invalidating `events.list()` must not be the
  // only thing that refreshes it — and the home widgets read it as one unit.
  // The upcoming-events widget invalidates this key directly on pull-to-
  // refresh; see ForYouContent's handleRefresh.
  homeCalendar: {
    all: ['homeCalendar'],
  },
  gameScoreboard: {
    all: ['gameScoreboard'],
    byGame: (gameId) => [...queryKeys.gameScoreboard.all, gameId],
  },
  gameSettings: {
    all: ['gameSettings'],
    byGame: (gameId) => [...queryKeys.gameSettings.all, gameId],
  },
  //  A competition's own settings, read when creating a game under it.
  scheduleSettings: {
    all: ['scheduleSettings'],
    bySchedule: (scheduleId) => [...queryKeys.scheduleSettings.all, scheduleId],
  },
  orgCompetitions: {
    all: ['orgCompetitions'],
    byOrg: (orgId) => [...queryKeys.orgCompetitions.all, orgId],
    bySearch: (orgId, search, filters) => [
      ...queryKeys.orgCompetitions.byOrg(orgId),
      search,
      // filters object is stable-stringified by react-query's key hashing,
      // so different sport filter combos get distinct cache entries.
      filters ?? null,
    ],
  },
  // Admin player search (roster "Add Player" search box). Keyed by org + query +
  // global flag so each settled (debounced) query caches independently.
  adminPlayerSearch: {
    all: ['adminPlayerSearch'],
    byQuery: (organizationId, query, isGlobal) => [
      ...queryKeys.adminPlayerSearch.all,
      organizationId ?? null,
      query,
      !!isGlobal,
    ],
  },
  teamSearch: {
    all: ['teamSearch'],
    notRegistered: (scheduleId, query, isGlobal) => [
      ...queryKeys.teamSearch.all,
      'notRegistered',
      scheduleId,
      query,
      isGlobal,
    ],
    // Operations add-team: the legacy org-scoped search. `isGlobal` widens the
    // scope to the root organization and changes the endpoint, so it is part
    // of the key rather than a param on one shared entry. The sport filters
    // narrow the RESULTS, so they belong here too — otherwise two competitions
    // of different sports under one org would share a cache entry.
    byOrg: (
      orgId,
      rootScheduleId,
      query,
      isGlobal,
      page,
      sportId,
      genericSportId
    ) => [
      ...queryKeys.teamSearch.all,
      'byOrg',
      orgId,
      rootScheduleId,
      query,
      !!isGlobal,
      page,
      sportId ?? null,
      genericSportId ?? null,
    ],
  },
  teamRosterChoices: {
    all: ['teamRosterChoices'],
    byTeam: (teamId) => [...queryKeys.teamRosterChoices.all, teamId],
  },
  // Scoring-context settings: same endpoint as gameSettings, but stored
  // verbatim from req() (no unwrapping) to match what ScoringContext's
  // setSettings(data) has stored historically. Keep this separate from
  // gameSettings so we don't fight over the cache shape with the existing
  // common/features/game/hooks/useGameSettings hook (which unwraps).
  scoringContextSettings: {
    all: ['scoringContextSettings'],
    byGame: (gameId) => [...queryKeys.scoringContextSettings.all, gameId],
  },
  gameHighlights: {
    all: ['gameHighlights'],
    byGame: (gameId) => [...queryKeys.gameHighlights.all, gameId],
  },
  gameShootout: {
    all: ['gameShootout'],
    byGame: (gameId) => [...queryKeys.gameShootout.all, gameId],
  },
  // Relationship vocabulary (GET /types/relationship). Static reference data —
  // cached 1h and shared by the family invite/edit + roster link-to-existing
  // pickers across web + mobile.
  relationshipTypes: {
    all: ['relationshipTypes'],
    // Same endpoint but keeps the "Me"/Self entry (edit-family flow only). Kept
    // under a distinct key so it never collides with the filtered `all` cache.
    allWithSelf: ['relationshipTypes', 'withSelf'],
  },
  // Team-official role vocabulary per sport (GET /sports/:id/types, narrowed to
  // team_official_types). Static reference data — cached 1h. Used to name the
  // roles in the registration team picker.
  teamOfficialTypes: {
    all: ['teamOfficialTypes'],
    bySport: (sportId) => [...queryKeys.teamOfficialTypes.all, sportId ?? null],
  },
  // Provinces/states for a country (GET /countries/:id/provinces). Geo data
  // never changes, so this is cached aggressively (1h staleTime) and shared
  // across mobile + web edit-player and roster-player flows.
  provinces: {
    all: ['provinces'],
    byCountry: (countryId) => [...queryKeys.provinces.all, countryId ?? null],
  },
  // SN-4709 Team Balance (team cost distribution). The tab-gating probe and the
  // tab content share the sheet key, so gating costs no extra fetch.
  //
  // D40 — the sheet is ROSTER-scoped, so every sheet-derived key carries the
  // rosterId. Without it, switching rosters served the previous roster's cached
  // sheet (cross-roster bleed) and a lock preview taken on roster A could be
  // replayed against roster B.
  teamBalance: {
    all: ['teamBalance'],
    // Roster DISCOVERY ("which rosters do I have balance on?") is team-wide, but
    // its answer is per-caller, so it is keyed on the auth user id. Both parts
    // are stable primitives: authUserId is not a token or mutable session
    // handle, and the whole cache is cleared on login, so a different user can
    // never read this entry.
    rosters: (teamId, authUserId) => [
      ...queryKeys.teamBalance.all,
      'rosters',
      teamId,
      authUserId ?? null,
    ],
    byTeamRoster: (teamId, rosterId) => [
      ...queryKeys.teamBalance.all,
      teamId,
      rosterId ?? null,
    ],
    // R4-#4: the exact server lock preview for one slot (fetched when the Lock modal opens).
    lockPreview: (teamId, rosterId, slotId) => [
      ...queryKeys.teamBalance.byTeamRoster(teamId, rosterId),
      'lock-preview',
      slotId,
    ],
    // SN-8310: the exact charge Add Invoice would create for one amount on one slot.
    additionalInvoicePreview: (teamId, rosterId, slotId, amountCents) => [
      ...queryKeys.teamBalance.byTeamRoster(teamId, rosterId),
      'additional-invoice-preview',
      slotId,
      amountCents,
    ],
    // Jeff, Oct 1 — one previous season's players, as they stand on THIS sheet
    // (invited, registered…). Inside the sheet's tree on purpose: every sheet write
    // can change those standings, so the next open re-reads them.
    importSeasonPlayers: (teamId, rosterId, registrationId, seasonId) => [
      ...queryKeys.teamBalance.byTeamRoster(teamId, rosterId),
      'import-season-players',
      registrationId ?? null,
      seasonId ?? null,
    ],
  },
  // Inon, Oct 2 — a MUTATION key, not a query key: the sheet's double-click Position /
  // Number saves. The sheet reads every save in flight back from the mutation cache
  // (useRosterDetailSaves), because one useMutation observer tracks only its latest
  // call — a second row's save would otherwise end the first row's pending state.
  teamBalanceRosterDetails: {
    save: (teamId, rosterId) => [
      'teamBalanceRosterDetails',
      'save',
      teamId ?? null,
      rosterId ?? null,
    ],
  },
  // Jeff, Oct 1 — the previous seasons a rep may import players from. Kept OUT of the
  // teamBalance tree, like teamPlayerFinder: every sheet write invalidates that whole
  // tree, and the seasons a rep played don't change when a share does — the import
  // line under the sheet would refetch after every edit.
  teamBalanceImport: {
    all: ['teamBalanceImport'],
    seasons: (teamId, rosterId, registrationId) => [
      ...queryKeys.teamBalanceImport.all,
      'seasons',
      teamId,
      rosterId ?? null,
      registrationId ?? null,
    ],
  },
  // GET /teams/:id/rosters — every roster (one per season) of a team. The Team
  // page Registration tab's Season picker and its Billing sub-tab share it.
  teamRosters: {
    all: ['teamRosters'],
    byTeam: (teamId) => [...queryKeys.teamRosters.all, teamId ?? null],
  },
  // SN-4709 × SN-8081 — the Team page's Player Finder settings (GET
  // /teams/:id/player-finder). Also the Team page's tab probe. The entries are the
  // CALLER's grants, so the key carries the auth user id, like balance discovery.
  teamPlayerFinder: {
    all: ['teamPlayerFinder'],
    byTeam: (teamId, authUserId) => [
      ...queryKeys.teamPlayerFinder.all,
      teamId ?? null,
      authUserId ?? null,
    ],
  },
  spareCandidates: {
    all: ['spareCandidates'],
    // SN-8309 — the game's eligibility list is per game AND team;
    // SN-8295 — narrowed by location (card location / roster spare address).
    byGame: (gameId, teamId, date, positionId, genderId, location) => [
      ...queryKeys.spareCandidates.all,
      gameId ?? null,
      teamId ?? null,
      date ?? null,
      positionId ?? null,
      genderId ?? null,
      location?.countryId || null,
      location?.provinceId || null,
      location?.city?.trim().toLowerCase() || null,
    ],
  },
  // Admin-scoped registration payload (GET /registration/:scheduleId/admin).
  // Distinct from the legacy non-RQ registration fetch in useRegistrationData —
  // this is the consolidated admin view intended to replace the modal's
  // separate registration/account/waivers/analytics calls.
  registrationAdmin: {
    all: ['registrationAdmin'],
    bySchedule: (scheduleId) => [
      ...queryKeys.registrationAdmin.all,
      scheduleId ?? null,
    ],
  },
  // One page of "who am I about to email" (POST /social-feed/:id/:type/recipients).
  // `fingerprint` is a plain object of the criteria plus paging/filter state; react-query
  // stable-hashes it, so two filter combinations can never collide and a stale page can
  // never render under a newer key.
  socialFeedRecipients: {
    all: ['socialFeedRecipients'],
    byFingerprint: (fingerprint) => [
      ...queryKeys.socialFeedRecipients.all,
      fingerprint ?? null,
    ],
  },
}
